import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  FormControl,
  FormLabel,
  useColorModeValue,
  useToast,
  Alert,
  AlertIcon,
  AlertDescription,
  Code,
  IconButton,
  InputGroup,
  InputRightElement,
  Divider,
  Switch,
  Badge,
} from "@chakra-ui/react";
import { ViewIcon, ViewOffIcon, CopyIcon } from "@chakra-ui/icons";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase/config";
import { useAuth } from "../contexts/AuthContext";
import PageShell from "../layout/PageShell";

const STRIPE_CONFIG_COLLECTION = "stripeConfig";
const STRIPE_CONFIG_DOC_ID = "global";
const STRIPE_WEBHOOK_URL = "https://northamerica-northeast1-fiverrrms1.cloudfunctions.net/stripeWebhook";

interface StripeConfig {
  // Test mode keys
  testSecretKey?: string;
  testPublicKey?: string;
  testWebhookSecret?: string;
  // Production mode keys
  prodSecretKey?: string;
  prodPublicKey?: string;
  prodWebhookSecret?: string;
  // Current mode
  mode?: "test" | "prod";
  lastUpdated?: any;
  updatedBy?: string;
}

const StripeSettings: React.FC = () => {
  const toast = useToast();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<"test" | "prod">("test");
  const [secretKey, setSecretKey] = useState("");
  const [publicKey, setPublicKey] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [showPublicKey, setShowPublicKey] = useState(false);
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);

  const bgCard = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const textColor = useColorModeValue("gray.700", "white");
  const textSecondary = useColorModeValue("gray.500", "gray.400");
  const codeBg = useColorModeValue("gray.100", "gray.800");

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const configDocRef = doc(db, STRIPE_CONFIG_COLLECTION, STRIPE_CONFIG_DOC_ID);
        const configDoc = await getDoc(configDocRef);

        if (configDoc.exists()) {
          const data = configDoc.data() as StripeConfig;
          const currentMode = data.mode || "test";
          setMode(currentMode);
          
          // Load keys based on current mode
          if (currentMode === "test") {
            setSecretKey(data.testSecretKey || "");
            setPublicKey(data.testPublicKey || "");
            setWebhookSecret(data.testWebhookSecret || "");
          } else {
            setSecretKey(data.prodSecretKey || "");
            setPublicKey(data.prodPublicKey || "");
            setWebhookSecret(data.prodWebhookSecret || "");
          }
        }
      } catch (error) {
        console.error("Error loading Stripe config:", error);
      }
    };

    loadConfig();
  }, []);

  // Update keys when mode changes
  useEffect(() => {
    const loadKeysForMode = async () => {
      try {
        const configDocRef = doc(db, STRIPE_CONFIG_COLLECTION, STRIPE_CONFIG_DOC_ID);
        const configDoc = await getDoc(configDocRef);

        if (configDoc.exists()) {
          const data = configDoc.data() as StripeConfig;
          if (mode === "test") {
            setSecretKey(data.testSecretKey || "");
            setPublicKey(data.testPublicKey || "");
            setWebhookSecret(data.testWebhookSecret || "");
          } else {
            setSecretKey(data.prodSecretKey || "");
            setPublicKey(data.prodPublicKey || "");
            setWebhookSecret(data.prodWebhookSecret || "");
          }
        }
      } catch (error) {
        console.error("Error loading keys for mode:", error);
      }
    };

    loadKeysForMode();
  }, [mode]);

  const handleSave = async () => {
    if (!secretKey.trim()) {
      toast({
        title: "Validation Error",
        description: "Stripe Secret Key is required",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!publicKey.trim()) {
      toast({
        title: "Validation Error",
        description: "Stripe Public Key is required",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    try {
      const configDocRef = doc(db, STRIPE_CONFIG_COLLECTION, STRIPE_CONFIG_DOC_ID);
      const configDoc = await getDoc(configDocRef);
      const existingData = configDoc.exists() ? (configDoc.data() as StripeConfig) : {};

      // Prepare update object - preserve keys from other mode
      const updateData: any = {
        mode: mode,
        lastUpdated: serverTimestamp(),
        updatedBy: currentUser?.email || "unknown",
      };

      if (mode === "test") {
        updateData.testSecretKey = secretKey.trim();
        updateData.testPublicKey = publicKey.trim();
        updateData.testWebhookSecret = webhookSecret.trim() || "";
        // Preserve prod keys if they exist
        if (existingData.prodSecretKey) updateData.prodSecretKey = existingData.prodSecretKey;
        if (existingData.prodPublicKey) updateData.prodPublicKey = existingData.prodPublicKey;
        if (existingData.prodWebhookSecret) updateData.prodWebhookSecret = existingData.prodWebhookSecret;
      } else {
        updateData.prodSecretKey = secretKey.trim();
        updateData.prodPublicKey = publicKey.trim();
        updateData.prodWebhookSecret = webhookSecret.trim() || "";
        // Preserve test keys if they exist
        if (existingData.testSecretKey) updateData.testSecretKey = existingData.testSecretKey;
        if (existingData.testPublicKey) updateData.testPublicKey = existingData.testPublicKey;
        if (existingData.testWebhookSecret) updateData.testWebhookSecret = existingData.testWebhookSecret;
      }

      await setDoc(configDocRef, updateData, { merge: true });

      toast({
        title: "Success",
        description: `Stripe ${mode === "test" ? "Test" : "Production"} configuration saved successfully`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      console.error("Error saving Stripe config:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to save Stripe configuration",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCopyWebhookUrl = () => {
    navigator.clipboard.writeText(STRIPE_WEBHOOK_URL);
    toast({
      title: "Copied",
      description: "Webhook URL copied to clipboard",
      status: "success",
      duration: 2000,
      isClosable: true,
    });
  };

  return (
    <PageShell
      title="Stripe Configuration"
      description="Keys used for subscription billing across every store."
    >
      <VStack spacing={6} align="stretch">

        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <AlertDescription>
            Configure your Stripe API keys to enable subscription management for all stores. 
            These keys are stored securely in Firestore and used globally across the platform.
          </AlertDescription>
        </Alert>

        <Box bg={bgCard} borderRadius="lg" boxShadow="sm" border="1px" borderColor={borderColor} p={6}>
          <VStack spacing={6} align="stretch">
            <FormControl>
              <HStack justify="space-between" align="center">
                <VStack align="start" spacing={1}>
                  <FormLabel mb={0}>Environment Mode</FormLabel>
                  <Text fontSize="xs" color={textSecondary}>
                    Switch between Test and Production keys
                  </Text>
                </VStack>
                <HStack spacing={3}>
                  <Badge colorScheme={mode === "test" ? "blue" : "gray"} fontSize="sm" px={3} py={1}>
                    Test
                  </Badge>
                  <Switch
                    isChecked={mode === "prod"}
                    onChange={(e) => setMode(e.target.checked ? "prod" : "test")}
                    colorScheme="green"
                    size="md"
                  />
                  <Badge colorScheme={mode === "prod" ? "green" : "gray"} fontSize="sm" px={3} py={1}>
                    Production
                  </Badge>
                </HStack>
              </HStack>
            </FormControl>

            <Divider />

            <FormControl>
              <FormLabel>Stripe Secret Key ({mode === "test" ? "Test" : "Production"})</FormLabel>
              <InputGroup>
                <Input
                  type={showSecretKey ? "text" : "password"}
                  value={secretKey}
                  onChange={(e) => setSecretKey(e.target.value)}
                  placeholder={mode === "test" ? "sk_test_..." : "sk_live_..."}
                  bg="white"
                />
                <InputRightElement width="4.5rem">
                  <IconButton
                    h="1.75rem"
                    size="sm"
                    aria-label={showSecretKey ? "Hide secret key" : "Show secret key"}
                    icon={showSecretKey ? <ViewOffIcon /> : <ViewIcon />}
                    onClick={() => setShowSecretKey(!showSecretKey)}
                  />
                </InputRightElement>
              </InputGroup>
            </FormControl>

            <FormControl>
              <FormLabel>Stripe Public Key ({mode === "test" ? "Test" : "Production"})</FormLabel>
              <InputGroup>
                <Input
                  type={showPublicKey ? "text" : "password"}
                  value={publicKey}
                  onChange={(e) => setPublicKey(e.target.value)}
                  placeholder={mode === "test" ? "pk_test_..." : "pk_live_..."}
                  bg="white"
                />
                <InputRightElement width="4.5rem">
                  <IconButton
                    h="1.75rem"
                    size="sm"
                    aria-label={showPublicKey ? "Hide public key" : "Show public key"}
                    icon={showPublicKey ? <ViewOffIcon /> : <ViewIcon />}
                    onClick={() => setShowPublicKey(!showPublicKey)}
                  />
                </InputRightElement>
              </InputGroup>
            </FormControl>

            <FormControl>
              <FormLabel>Webhook Secret ({mode === "test" ? "Test" : "Production"})</FormLabel>
              <InputGroup>
                <Input
                  type={showWebhookSecret ? "text" : "password"}
                  value={webhookSecret}
                  onChange={(e) => setWebhookSecret(e.target.value)}
                  placeholder="whsec_..."
                  bg="white"
                />
                <InputRightElement width="4.5rem">
                  <IconButton
                    h="1.75rem"
                    size="sm"
                    aria-label={showWebhookSecret ? "Hide webhook secret" : "Show webhook secret"}
                    icon={showWebhookSecret ? <ViewOffIcon /> : <ViewIcon />}
                    onClick={() => setShowWebhookSecret(!showWebhookSecret)}
                  />
                </InputRightElement>
              </InputGroup>
            </FormControl>

            <Divider />

            <FormControl>
              <FormLabel>Webhook Endpoint URL</FormLabel>
              <HStack>
                <Code
                  p={3}
                  bg={codeBg}
                  borderRadius="md"
                  flex={1}
                  fontSize="sm"
                  wordBreak="break-all"
                >
                  {STRIPE_WEBHOOK_URL}
                </Code>
                <IconButton
                  aria-label="Copy webhook URL"
                  icon={<CopyIcon />}
                  onClick={handleCopyWebhookUrl}
                />
              </HStack>
              <Text fontSize="xs" color={textSecondary} mt={2}>
                Configure this URL in your Stripe Dashboard under Webhooks
              </Text>
            </FormControl>

            <Button alignSelf="flex-start" onClick={handleSave} isLoading={loading} loadingText="Saving...">
              Save configuration
            </Button>
          </VStack>
        </Box>
      </VStack>
    </PageShell>
  );
};

export default StripeSettings;

